import { useState } from "react";
import { Pager } from "@/app/Pager";
import { useCanWrite } from "@/app/auth";
import { deleteTarget } from "@/app/deleteTarget";
import { EntityDrawer } from "@/app/EntityDrawer";
import { EntityStatusPill } from "@/app/EntityStatusPill";
import { accounts, CHARGE_ON, customers, type Customer, type Input } from "@/app/master";
import { loadingOf, useList } from "@/app/queries";
import { usePage } from "@/app/usePage";
import { Alert, Button, EmptyState, Input as TextInput, PageHeader, Table, TableWrap, Th, Toolbar } from "@/ui";
import { CustomerForm } from "./CustomerForm";
import { customerDraft, customerInput } from "./customerDraft";

/** 抽屉里正在编辑的客户：null 表示新建，undefined 表示抽屉关闭。 */
type Editing = Customer | null | undefined;

/** 客户管理：客户列表，新建与编辑在右侧抽屉里；接入账号在“客户账号”页（docs/04 第 14 节）。 */
export function Customers() {
  const canWrite = useCanWrite(); // 只读账号不显示写操作，服务端同样拒绝（docs/04 第 9 节）
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Editing>(undefined);
  const customerList = useList("customers", customers);
  const accountList = useList("accounts", accounts); // 已有账号的客户不能改币种

  const keyword = q.trim().toLowerCase();
  const shown = (customerList.data ?? []).filter((c) => !keyword || c.name.toLowerCase().includes(keyword));
  const page = usePage("customers", shown, keyword);

  return (
    <>
      <PageHeader title="客户管理" description="客户的币种、计费方式与档位，保存即生效；接入账号在“客户账号”管理。"
        actions={canWrite && <Button variant="primary" onClick={() => setEditing(null)}>新建客户</Button>} />
      {customerList.error && <Alert tone="danger" title="加载失败">{customerList.error.message}</Alert>}
      <Toolbar>
        <TextInput size="sm" className="ax-search" placeholder="搜索客户名称" aria-label="搜索" value={q} onChange={(e) => setQ(e.target.value)} />
      </Toolbar>
      <TableWrap loading={loadingOf(customerList)}>
        {shown.length ? (
          <>
            <Table>
              <thead><tr><Th w="id">ID</Th><Th w="name">名称</Th><Th w={72}>币种</Th><Th w={104}>计费方式</Th><Th w={96}>档位</Th><Th w="status">状态</Th><Th w="wide">备注</Th><Th /></tr></thead>
              <tbody>
                {page.rows.map((c) => (
                  <tr key={c.id}>
                    <td className="ax-mono">{c.id}</td><td>{c.name}</td><td className="ax-mono">{c.currency}</td><td>{CHARGE_ON[c.charge_on]}</td><td>{c.tier}</td>
                    <td><EntityStatusPill status={c.status} /></td><td className="ax-secondary">{c.note}</td>
                    <td><Button variant="ghost" size="sm" onClick={() => setEditing(c)}>{canWrite ? "编辑" : "查看"}</Button></td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pager {...page} />
          </>
        ) : (
          !customerList.isPending && <EmptyState title="没有客户">新建客户后再到“客户账号”为它添加接入账号。</EmptyState>
        )}
      </TableWrap>
      {editing !== undefined && (
        <EntityDrawer title={editing ? `编辑客户 ${editing.name}` : "新建客户"} initial={customerDraft(editing ?? undefined)} convert={customerInput}
          save={(body: Input<Customer>) => (editing ? customers.update(editing.id, body) : customers.create(body))}
          invalidate={["customers"]} message={editing ? "已更新客户" : "已新建客户"} deletion={editing ? deleteTarget("客户", editing.name, customers, editing.id, ["customers"]) : undefined} onClose={() => setEditing(undefined)}>
          {(f) => <CustomerForm currencyReadOnly={!!editing && (accountList.isPending || accountList.isError || !!accountList.data?.some((a) => a.customer_id === editing.id))} draft={f.draft} errors={f.errors} set={f.set} isNew={!editing} />}
        </EntityDrawer>
      )}
    </>
  );
}
